import { useState } from 'react'

import deepmerge from 'deepmerge'
import {
  GetExpensesDatatableExportDocument,
  GetProjectsDatatableExportDocument,
  GetShiftsDatatableExportDocument,
  GetSubprojectsDatatableExportDocument,
  GetTasksDatatableExportDocument,
} from 'generated/resources/datatable.graphql'
import { ProjectManagementEnumsExportableProjectDateGroupEnum } from 'generated/resources/graphql'
import { DocumentNode } from 'graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import asyncAction from 'modules/core/lib/asyncAction'
import { dateUIToBackend } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import apiClient from 'modules/core/lib/mobx-rest/apiClient'

import { FormValues } from 'modules/project_management/components/modals/ExportProjectsModal/types'
import {
  expensesExportProjections,
  IExportProjection,
  shiftsExportProjections,
  subprojectsExportProjections,
  tasksExportProjections,
  totalTimeByEmployeeProjections,
  totalTimeExportProjections,
} from 'modules/project_management/lib/exportsProjections'
import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'

type Props = {
  formValues: FormValues
  hasSubprojectsFeature: boolean
  hasClientsFeature: boolean
  showFinancials: boolean
}

export default function useProjectsExport({
  formValues,
  hasClientsFeature,
  hasSubprojectsFeature,
  showFinancials,
}: Props) {
  // Check if in future it's needed to use datatable filters
  // const { variables } = useDataTable()
  const variables = {}

  const [isExporting, setIsExporting] = useState(false)

  const { company } = useCurrentSession()

  const getStartDate = () => {
    if (formValues.from_date) return dateUIToBackend(formValues.from_date)
    if (formValues.date_range === 'last_90_days')
      return moment().subtract(90, 'days').format('YYYY-MM-DD')
    if (formValues.date_range === 'current_month')
      return moment().startOf('month').format('YYYY-MM-DD')
    if (formValues.date_range === 'last_month')
      return moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD')
  }
  const getEndDate = () => {
    if (formValues.to_date) return dateUIToBackend(formValues.to_date)
    if (formValues.date_range === 'current_month' || formValues.date_range === 'last_90_days')
      return moment().endOf('month').format('YYYY-MM-DD')
    if (formValues.date_range === 'last_month')
      return moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD')
  }

  interface queryParams {
    startDate?: string
    endDate?: string
    timeFormat?: string
    projectIds?: string[]
    employeeIds?: string[]
    employeeSelection?: EmployeeSelectorV2MultiSelectState
    includeSubprojects?: boolean
    includeDate?: boolean
    sortByEmployee?: boolean
    companyId?: string
    includeClient?: boolean
    includeEmployeesWithoutTimeTracked?: boolean
    dateGroup?: ProjectManagementEnumsExportableProjectDateGroupEnum
    includeCost?: boolean
    includeProfitability?: boolean
    includeInputedMinutes?: boolean
  }

  interface exportProjectsData {
    projections: IExportProjection[]
    query: DocumentNode
    queryParams: queryParams
  }

  const is_number_type = formValues.time_format == 'hours'

  const getExportData = (): exportProjectsData => {
    switch (formValues.export_type) {
      case 'total_time':
      case 'total_time_by_date':
      case 'total_time_and_cost_by_month':
        return {
          projections: totalTimeExportProjections(
            ['total_time_by_date', 'total_time_and_cost_by_month'].includes(formValues.export_type),
            hasSubprojectsFeature,
            is_number_type,
            hasClientsFeature,
            formValues.export_type === 'total_time_and_cost_by_month'
          ),
          query: GetProjectsDatatableExportDocument,
          queryParams: {
            startDate: getStartDate(),
            endDate: getEndDate(),
            timeFormat: formValues.time_format,
            projectIds: formValues.project_ids,
            includeSubprojects: hasSubprojectsFeature,
            includeDate: ['total_time_by_date', 'total_time_and_cost_by_month'].includes(
              formValues.export_type
            ),
            companyId: company.id,
            includeClient: hasClientsFeature,
            includeEmployeesWithoutTimeTracked: formValues.include_employees_without_time_tracked,
            dateGroup: formValues.export_type === 'total_time_and_cost_by_month' ? 'month' : 'day',
          },
        }
      case 'total_time_by_employee':
        return {
          projections: totalTimeByEmployeeProjections(hasSubprojectsFeature, is_number_type),
          query: GetShiftsDatatableExportDocument,
          queryParams: {
            startDate: getStartDate(),
            endDate: getEndDate(),
            timeFormat: formValues.time_format,
            employeeIds: formValues.employee_ids,
            employeeSelection: formValues.employee_selection,
            includeSubprojects: hasSubprojectsFeature,
            sortByEmployee: true,
          },
        }
      case 'shifts':
        return {
          projections: shiftsExportProjections(hasSubprojectsFeature, is_number_type),
          query: GetShiftsDatatableExportDocument,
          queryParams: {
            startDate: getStartDate(),
            endDate: getEndDate(),
            timeFormat: formValues.time_format,
            employeeIds: formValues.employee_ids,
            employeeSelection: formValues.employee_selection,
            includeSubprojects: hasSubprojectsFeature,
          },
        }
      case 'tasks':
        return {
          projections: tasksExportProjections(hasSubprojectsFeature),
          query: GetTasksDatatableExportDocument,
          queryParams: {
            startDate: getStartDate(),
            endDate: getEndDate(),
            projectIds: formValues.project_ids,
          },
        }
      case 'subprojects':
        return {
          projections: subprojectsExportProjections(showFinancials),
          query: GetSubprojectsDatatableExportDocument,
          queryParams: {
            projectIds: formValues.project_ids,
            includeCost: showFinancials,
            includeProfitability: showFinancials,
            includeInputedMinutes: true,
          },
        }
      case 'expenses':
      default:
        return {
          projections: expensesExportProjections(),
          query: GetExpensesDatatableExportDocument,
          queryParams: {
            startDate: getStartDate(),
            endDate: getEndDate(),
            projectIds: formValues.project_ids,
          },
        }
    }
  }

  const onExport = () => {
    if (isExporting) return

    const exportData = getExportData()

    const action = async () => {
      try {
        setIsExporting(true)

        await apiClient().post('/data_table/exports', {
          query: exportData.query.loc?.source.body,
          variables: deepmerge({ variables }, exportData.queryParams),
          projections: exportData.projections,
          fileFormat: formValues.file_format,
          fileName: formValues.name,
        })
        setIsExporting(false)
      } catch (error) {
        setIsExporting(false)
        throw error
      }
    }

    asyncAction(action, {
      successMessage: i18n.t('data_table.export.success'),
    })
  }

  return {
    onExport,
  }
}
